import {
  financeConfigInitialValues,
  financeConfigSchema,
} from "../../models/financeConfig";
import { sanitizeValues, toInitialValuesFromSchema } from "../../models/utils";
import useTransactions from "../../hooks/use-transactions";
import { useDebouncedState } from "../../hooks/use-debounced-value";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Selectable } from "../UI/Selectable";
import {
  BANK_AFFECTATION_TYPE_FILTERS,
  BalanceDomain,
  CONTEXTLESS_AFFECTATION_DOMAINS,
  DOMAIN_TO_PARENT_ID_FIELD_NAME,
} from "../../utils/constants/balances";
import useProperties from "../../hooks/use-properties";
import usePropertyFolders from "../../hooks/use-property-folders";
import useTenants from "../../hooks/use-tenants";
import { Form, Formik, useFormikContext } from "formik";
import BalanceTargetFields from "../Balance/BalanceTargetFields";
import TextInput from "../UI/TextInput";
import Tooltip from "../UI/Tooltip";
import { Interrogation } from "../UI/Icons";
import Spinner from "../UI/Spinner";
import Card from "../UI/Card";
import Callout from "../UI/Callout";
import { ModalDialogFooter } from "../UI/ModalDialog";
import moment from "moment/moment";
import { currencyFormatter } from "../../api/Functions";
import Button from "../UI/Button";
import FormikSubmitButton from "../UI/FormikSubmitButton";
import useBanks from "../../hooks/use-banks";
import BalanceSplitFields from "../Balance/BalanceSplitFields";
import { shouldSplitBalance } from "../../utils/balances";
import { handleAPIError } from "../../utils/index.jsx";

export default function FinanceConfigForm({
  config,
  tenantId,
  onSubmit,
  onDelete,
}) {
  let initialValues = toInitialValuesFromSchema(
    financeConfigSchema,
    config,
    financeConfigInitialValues,
  );

  const [debouncedKeyword, setKeyword] = useDebouncedState(
    initialValues.keyword,
  );
  const [debouncedAmount, setAmount] = useDebouncedState(initialValues.amount);
  const [accounts, setAccounts] = useState([]);
  const [filterNoProfileAccounts, setFilterNoProfileAccounts] = useState(false);
  const [error, setError] = useState("");

  if (tenantId) {
    // If tenant cannot be edited (e.g. edit from tenant page), manually add the id_tenant as it's required
    initialValues.id_tenant = tenantId;
    initialValues.domaine = "tenant";
    initialValues.affectation = `tenant:${tenantId}`;
  }

  const {
    transactions,
    isLoading: isLoadingTransactions,
    createTransactionBalances,
  } = useTransactions({
    params: {
      page: 1,
      per_page: 1000,
      search: debouncedKeyword || undefined,
      montant_min: debouncedAmount || undefined,
      montant_max: debouncedAmount || undefined,
      account: accounts.length > 0 ? accounts : undefined,
      filter_no_profile_accounts: filterNoProfileAccounts || undefined,
    },
  });

  return (
    <>
      <Selectable
        items={transactions.filter(
          (transaction) => !transaction.balances.length > 0,
        )}
      >
        <Selectable.Action
          render={(selected, resetSelected) => {
            async function submitHandler(values, { resetForm }) {
              const sanitizedValues = { ...sanitizeValues(values) };
              setError("");
              try {
                if (selected.length > 0) {
                  await createTransactionBalances(
                    selected[0],
                    [
                      {
                        id_tenant: sanitizedValues.id_tenant,
                        montant: transactions.find((t) => t.id === selected[0])
                          .montant,
                        type: sanitizedValues.type,
                        id_property_folder: sanitizedValues.id_property_folder,
                        id_bien: sanitizedValues.id_bien,
                        domaine: sanitizedValues.domaine,
                        affectation: sanitizedValues.affectation,
                      },
                    ],
                    {
                      similarTransactionIds: selected.slice(1),
                      repartitions: sanitizedValues.repartition,
                    },
                  );
                }
                await onSubmit(sanitizedValues);
                resetForm();
                resetSelected();
              } catch (e) {
                setError(handleAPIError(e));
              }
            }
            return (
              <InnerFormik
                onSubmit={submitHandler}
                onDelete={onDelete}
                initialValues={initialValues}
                transactions={transactions}
                isLoadingTransactions={isLoadingTransactions}
                tenantId={tenantId}
                debouncedKeyword={debouncedKeyword}
                setKeyword={setKeyword}
                setAmount={setAmount}
                setAccounts={setAccounts}
                setFilterNoProfileAccounts={setFilterNoProfileAccounts}
                config={config}
                error={error}
              />
            );
          }}
        />
      </Selectable>
    </>
  );
}

function InnerFormik({
  onSubmit,
  onDelete,
  initialValues,
  transactions,
  isLoadingTransactions,
  tenantId,
  debouncedKeyword,
  setKeyword,
  setAmount,
  setAccounts,
  setFilterNoProfileAccounts,
  config,
  error,
}) {
  const [showMore, setShowMore] = useState(false);
  const { tenantsById } = useTenants({ withById: true });
  const { propertiesById } = useProperties({ withById: true });
  const { propertyFoldersById } = usePropertyFolders({ withById: true });

  const { banks } = useBanks();
  const bankUniqueIdsByProfileBailleurId = useMemo(() => {
    return banks.reduce((acc, bank) => {
      if (!bank.id_profile_nom) return acc;
      if (!acc[bank.id_profile_nom]) {
        acc[bank.id_profile_nom] = [];
      }
      acc[bank.id_profile_nom].push(bank.unique_id);
      return acc;
    }, {});
  }, [banks]);

  const handleAffectationChange = useCallback(
    (domaine, parentId) => {
      if (!domaine || !parentId) {
        setAccounts([]);
        setFilterNoProfileAccounts(false);
        return;
      }
      let profileBailleurId;
      if (domaine === BalanceDomain.TENANT) {
        const tenant = tenantsById[parentId];
        profileBailleurId =
          tenant?.id_profile_bailleur ||
          propertiesById[tenant?.id_bien]?.id_profile_nom_owner;
      } else if (domaine === BalanceDomain.FOLDER) {
        profileBailleurId = propertyFoldersById[parentId]?.id_profile_nom_owner;
      } else if (domaine === BalanceDomain.PROPERTY) {
        profileBailleurId = propertiesById[parentId]?.id_profile_nom_owner;
      }
      if (profileBailleurId) {
        const accountsForProfile =
          bankUniqueIdsByProfileBailleurId[profileBailleurId] ?? [];
        if (accountsForProfile.length > 0) {
          setAccounts(accountsForProfile);
          setFilterNoProfileAccounts(false);
        } else {
          setAccounts([]);
          setFilterNoProfileAccounts(true);
        }
      } else {
        setAccounts([]);
        setFilterNoProfileAccounts(false);
      }
    },
    [
      bankUniqueIdsByProfileBailleurId,
      propertiesById,
      propertyFoldersById,
      setAccounts,
      setFilterNoProfileAccounts,
      tenantsById,
    ],
  );
  const affectationContext = useMemo(
    () =>
      tenantId
        ? { domaine: BalanceDomain.TENANT, parentId: tenantId }
        : undefined,
    [tenantId],
  );
  const initialDomaine = initialValues.domaine;
  const initialParentId =
    initialValues[DOMAIN_TO_PARENT_ID_FIELD_NAME[initialDomaine]];
  useEffect(() => {
    if (tenantId) {
      handleAffectationChange(BalanceDomain.TENANT, tenantId);
    } else if (initialDomaine && initialParentId) {
      handleAffectationChange(initialDomaine, parseInt(initialParentId));
    }
  }, [handleAffectationChange, initialDomaine, initialParentId, tenantId]);

  // Changer de cible vide la répartition, mais une configuration peut arriver de la
  // base avec une répartition sur une cible qui ne se répartit pas. Renvoyée telle
  // quelle, elle ventilerait les montants vers un autre dossier, au rapprochement
  // comme à chaque synchronisation bancaire qui applique la configuration.
  function submitWithRenderedRepartition(values, helpers) {
    const isSplit = shouldSplitBalance(
      values.domaine,
      values.id_property_folder,
      propertyFoldersById,
    );
    return onSubmit(isSplit ? values : { ...values, repartition: [] }, helpers);
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema={financeConfigSchema}
      onSubmit={submitWithRenderedRepartition}
    >
      {({ handleChange, setFieldValue, values }) => {
        const displayedSimilarTransactions = showMore
          ? transactions
          : transactions.slice(0, 5);
        const isSavedFolder =
          String(values.id_property_folder) ===
          String(initialValues.id_property_folder);

        // La répartition appartient au dossier sur lequel elle a été saisie : elle ne
        // survit qu'à une cible qui reste ce dossier. Un nouveau dossier à répartir est
        // réinitialisé par BalanceSplitFields à son montage.
        function changeAffectation(domaine, parentId) {
          const staysOnFolder =
            domaine === BalanceDomain.FOLDER &&
            String(parentId) === String(values.id_property_folder);
          if (!staysOnFolder) {
            setFieldValue("repartition", []);
          }
          handleAffectationChange(domaine, parentId);
        }
        return (
          <>
            <AffectationWatcher
              tenantId={tenantId}
              handleAffectationChange={handleAffectationChange}
              initialDomaine={initialDomaine}
              initialParentId={initialParentId}
            />
            <Form>
              <BalanceTargetFields
                context={affectationContext}
                isEdit={!!config}
                {...BANK_AFFECTATION_TYPE_FILTERS}
                restrictToDomains={CONTEXTLESS_AFFECTATION_DOMAINS}
                withFolders={!tenantId}
                onAffectationChange={!tenantId ? changeAffectation : undefined}
              />
              <TextInput
                type="text"
                label={"Mot clé à comparer"}
                name="keyword"
                placeholder="keyword"
                mandatory
                className={"marginT10"}
                onChange={(e) => {
                  handleChange(e);
                  setKeyword(e.target.value);
                }}
              />
              <TextInput
                type="number"
                step="0.01"
                min={null}
                label={
                  <>
                    Montant (optionnel){" "}
                    <Tooltip
                      content={
                        <>
                          Utile lorsque le locataire a deux baux (garage,
                          logement) et fait deux virements différents. Doit être
                          mis à jour si vous modifiez le montant du loyer.
                        </>
                      }
                    >
                      <Interrogation stroke={"#C78F00"} />
                    </Tooltip>
                  </>
                }
                name="montant"
                placeholder="15"
                className={"marginT10"}
                onChange={(e) => {
                  handleChange(e);
                  setAmount(e.target.value);
                }}
              />
              {shouldSplitBalance(
                values.domaine,
                values.id_property_folder,
                propertyFoldersById,
              ) && (
                <BalanceSplitFields
                  isFinanceConfig
                  name={"repartition"}
                  parentId={parseInt(values.id_property_folder)}
                  domaineBalance={BalanceDomain.FOLDER}
                  childBalances={
                    isSavedFolder && initialValues.repartition.length > 0
                      ? initialValues.repartition
                      : undefined
                  }
                />
              )}
              {debouncedKeyword && (
                <>
                  <p className={"primaryText marginB10"}>
                    Transactions existantes à affecter
                  </p>
                  {isLoadingTransactions && (
                    <p className={"text-center marginB20"}>
                      <Spinner /> Recherche de transactions correspondantes...
                    </p>
                  )}
                  {transactions.length === 0 && !isLoadingTransactions && (
                    <p className={"text-center marginB20"}>
                      Aucune transaction correspondante.
                    </p>
                  )}
                  {transactions.length > 0 && !isLoadingTransactions && (
                    <>
                      <div
                        className={"flex wrap gap10 space-between marginB10"}
                      >
                        <label>
                          <Selectable.Checkbox selectAll /> Tout sélectionner
                        </label>
                      </div>
                      {displayedSimilarTransactions.map((transaction, i) => (
                        <label key={transaction.id}>
                          <Card
                            padding={"md"}
                            className={"marginB5"}
                            type={
                              transaction.balances.length > 0 ? "disabled" : ""
                            }
                          >
                            <Selectable.Checkbox
                              value={transaction.id}
                              index={i}
                              disabled={transaction.balances.length > 0}
                              className={"marginR5"}
                            />
                            {moment(transaction.date_transac).format(
                              "DD/MM/YYYY",
                            )}{" "}
                            - {transaction.description} -{" "}
                            {currencyFormatter(transaction.montant)}
                            {transaction.balances.length > 0 && (
                              <div>
                                <AffectationDisplay transaction={transaction} />
                              </div>
                            )}
                          </Card>
                        </label>
                      ))}
                      {transactions.length > 5 && (
                        <div className={"text-center marginB20"}>
                          <Button
                            onClick={() => setShowMore(!showMore)}
                            type={"button"}
                          >
                            {showMore
                              ? "Afficher moins"
                              : `Afficher ${transactions.length - 5} de plus`}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
              <Callout variant="danger">{error}</Callout>
              <ModalDialogFooter>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!config?.id}
                  onClick={() => onDelete(config.id)}
                >
                  Supprimer
                </Button>
                <FormikSubmitButton>Valider</FormikSubmitButton>
              </ModalDialogFooter>
            </Form>
          </>
        );
      }}
    </Formik>
  );
}

function AffectationWatcher({
  tenantId,
  handleAffectationChange,
  initialDomaine,
  initialParentId,
}) {
  const { values } = useFormikContext();
  const currentDomaine = values.domaine || initialDomaine;
  const currentParentId =
    values[DOMAIN_TO_PARENT_ID_FIELD_NAME[currentDomaine]] || initialParentId;

  useEffect(() => {
    if (tenantId) {
      handleAffectationChange(BalanceDomain.TENANT, tenantId);
    } else if (currentDomaine && currentParentId) {
      handleAffectationChange(currentDomaine, parseInt(currentParentId));
    } else {
      handleAffectationChange(null, null);
    }
  }, [currentDomaine, currentParentId, tenantId, handleAffectationChange]);

  return null;
}

function AffectationDisplay({ transaction }) {
  const { propertiesById } = useProperties({ withById: true });
  const { propertyFoldersById } = usePropertyFolders({ withById: true });
  const { tenantsById } = useTenants({ withById: true });
  const affectations = new Set();

  transaction.balances.forEach((balance) => {
    if (balance.domaine === BalanceDomain.PROPERTY) {
      affectations.add(propertiesById[balance.id_bien]?.nom);
    } else if (balance.domaine === BalanceDomain.FOLDER) {
      affectations.add(propertyFoldersById[balance.id_property_folder]?.nom);
    } else if (balance.domaine === BalanceDomain.TENANT) {
      affectations.add(tenantsById[balance.id_tenant]?.nom);
    }
  });
  return <>Affectée à {[...affectations].join(", ")}</>;
}
